import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface AreaGroup {
  region: string;
  cities: string[];
}

export interface ServiceAreasProps {
  title: string;
  subtitle: string;
  areaGroups: AreaGroup[];
}

export default function ServiceAreas({ title, subtitle, areaGroups }: ServiceAreasProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal py-28 lg:py-40 relative overflow-hidden">
      {/* Background watermark */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-serif text-white/[0.025] select-none pointer-events-none leading-none"
        style={{ fontSize: 'clamp(5rem, 16vw, 16rem)', fontWeight: 700 }}
      >
        FLORIDA
      </div>

      <div className="relative z-10 mx-auto px-6 lg:px-12 xl:px-20">
        {/* Heading */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                Service Areas
              </span>
            </div>
            <h2
              className="font-serif text-white leading-tight"
              style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.8rem)', fontWeight: 300 }}
            >
              {title}
            </h2>
          </div>
          <p className="text-white/40 text-sm leading-[1.85] max-w-xs lg:text-right">
            {subtitle}
          </p>
        </div>

        {/* Geographic hierarchy grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/8">
          {areaGroups.map((group, i) => (
            <AreaGroupCard key={group.region} group={group} index={i} last={i === areaGroups.length - 1} />
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-10 flex items-center gap-5">
          <span className="h-px flex-1 bg-white/6 max-w-xs" />
          <div className="flex items-center gap-2">
            <i className="ri-earth-line text-gold/50 text-sm"></i>
            <p className="text-white/60 text-xs tracking-wider">
              Nationwide project experience beyond Florida service area
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreaGroupCard({
  group,
  index,
  last,
}: {
  group: AreaGroup;
  index: number;
  last: boolean;
}) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`p-7 lg:p-8 transition-all duration-700 ease-out hover:bg-white/[0.03] ${
        !last ? 'border-b sm:border-b-0 sm:border-r border-white/8' : ''
      } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {/* Region header */}
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/8">
        <span className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
        <h3 className="text-gold text-[9px] font-bold tracking-[0.3em] uppercase">
          {group.region}
        </h3>
      </div>

      {/* Cities list */}
      <ul className="space-y-2.5">
        {group.cities.map((city) => (
          <li key={city} className="flex items-center gap-2.5 group">
            <span className="w-px h-3 bg-white/15 group-hover:bg-gold/40 transition-colors duration-300 flex-shrink-0" />
            <span className="text-white/50 group-hover:text-white/80 text-xs font-medium tracking-wide transition-colors duration-300">
              {city}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
